/**
 * Social Media Service - CRUD operations for social media posts
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { getCurrentDb } from '../config/firebaseManager';

// Platform types
export type SocialPlatform = 'facebook' | 'instagram' | 'linkedin' | 'tiktok';

// Post status types
export type PostStatus = 'draft' | 'scheduled' | 'published' | 'failed';

// Social Media Post interface
export interface SocialMediaPost {
  id?: string;
  content: string;
  platforms: SocialPlatform[];
  images: string[];
  videos: string[];
  status: PostStatus;
  scheduledFor?: Timestamp | null;
  publishedAt?: Timestamp | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  hashtags: string[];
  platformContent?: {
    facebook?: { content: string; link?: string };
    instagram?: { caption: string; firstComment?: string };
    linkedin?: { content: string; title?: string };
    tiktok?: { caption: string; videoUrl: string };
  };
  publishResults?: {
    facebook?: { postId?: string; status: 'success' | 'failed'; error?: string; publishedAt?: Timestamp };
    instagram?: { postId?: string; status: 'success' | 'failed'; error?: string; publishedAt?: Timestamp };
    linkedin?: { postId?: string; status: 'success' | 'failed'; error?: string; publishedAt?: Timestamp };
    tiktok?: { postId?: string; status: 'success' | 'failed'; error?: string; publishedAt?: Timestamp };
  };
}

// Character limits by platform
export const PLATFORM_LIMITS = {
  facebook: { chars: 63206, recommended: 80 },
  instagram: { chars: 2200, recommended: 125 },
  linkedin: { chars: 3000, recommended: 140 },
  tiktok: { chars: 2200, recommended: 100 }
};

// Helper to get Firestore DB instance
const getDb = () => {
  const db = getCurrentDb();
  if (!db) {
    throw new Error("Firestore is not initialized. Please ensure Firebase is configured.");
  }
  return db;
};

/**
 * Get all social media posts with optional filters
 */
export const getAllPosts = async (
  filters: { 
    status?: PostStatus; 
    platform?: SocialPlatform; 
    searchTerm?: string;
    createdBy?: string;
  } = {},
  sortField: keyof SocialMediaPost = 'createdAt',
  sortDirection: 'asc' | 'desc' = 'desc'
): Promise<SocialMediaPost[]> => {
  try {
    const postsRef = collection(getDb(), 'socialMediaPosts');
    let q = query(postsRef);

    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.platform) {
      q = query(q, where('platforms', 'array-contains', filters.platform));
    }
    if (filters.createdBy) {
      q = query(q, where('createdBy', '==', filters.createdBy));
    }

    q = query(q, orderBy(sortField, sortDirection));

    const snapshot = await getDocs(q);
    let posts: SocialMediaPost[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data() as Omit<SocialMediaPost, 'id'>
    }));

    // Client-side search filter if provided
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      posts = posts.filter(post =>
        post.content.toLowerCase().includes(searchLower) ||
        post.hashtags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    return posts;
  } catch (error) {
    console.error('Error getting social media posts:', error);
    return [];
  }
};

/**
 * Get a single post by ID
 */
export const getPostById = async (id: string): Promise<SocialMediaPost | null> => {
  try {
    const postRef = doc(getDb(), 'socialMediaPosts', id);
    const postSnap = await getDoc(postRef);
    if (postSnap.exists()) {
      return { id: postSnap.id, ...postSnap.data() as Omit<SocialMediaPost, 'id'> };
    }
    return null;
  } catch (error) {
    console.error(`Error getting post by ID ${id}:`, error);
    return null;
  }
};

/**
 * Create a new social media post
 */
export const createPost = async (
  postData: Omit<SocialMediaPost, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string | null> => {
  try {
    // Validate required fields
    if (!postData.content || !postData.platforms || postData.platforms.length === 0) {
      throw new Error('Content and at least one platform are required');
    }

    // Validate character limits
    const contentLength = postData.content.length;
    for (const platform of postData.platforms) {
      if (contentLength > PLATFORM_LIMITS[platform].chars) {
        throw new Error(`Content exceeds ${platform} character limit (${PLATFORM_LIMITS[platform].chars} chars)`);
      }
    }

    const postsRef = collection(getDb(), 'socialMediaPosts');
    const newPost = {
      ...postData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      images: postData.images || [],
      videos: postData.videos || [],
      hashtags: postData.hashtags || [],
      scheduledFor: postData.status === 'scheduled' && postData.scheduledFor ? postData.scheduledFor : null,
      publishedAt: postData.status === 'published' ? serverTimestamp() : null,
    };

    const docRef = await addDoc(postsRef, newPost);
    return docRef.id;
  } catch (error) {
    console.error('Error creating social media post:', error);
    throw error;
  }
};

/**
 * Update an existing post
 */
export const updatePost = async (
  id: string, 
  updates: Partial<Omit<SocialMediaPost, 'id' | 'createdAt'>>
): Promise<void> => {
  try {
    const postRef = doc(getDb(), 'socialMediaPosts', id);
    
    // Validate character limits if content is being updated
    if (updates.content && updates.platforms) {
      const contentLength = updates.content.length;
      for (const platform of updates.platforms) {
        if (contentLength > PLATFORM_LIMITS[platform].chars) {
          throw new Error(`Content exceeds ${platform} character limit`);
        }
      }
    }

    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    // Handle publishedAt when status changes to published
    if (updates.status === 'published') {
      updateData.publishedAt = serverTimestamp();
    } else if (updates.status === 'draft') {
      updateData.publishedAt = null;
      updateData.scheduledFor = null;
    }

    await updateDoc(postRef, updateData);
  } catch (error) {
    console.error(`Error updating post ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a post
 */
export const deletePost = async (id: string): Promise<void> => {
  try {
    const postRef = doc(getDb(), 'socialMediaPosts', id);
    await deleteDoc(postRef);
  } catch (error) {
    console.error(`Error deleting post ${id}:`, error);
    throw error;
  }
};

/**
 * Get posts by status
 */
export const getPostsByStatus = async (status: PostStatus): Promise<SocialMediaPost[]> => {
  return getAllPosts({ status });
};

/**
 * Get scheduled posts that are ready to publish
 */
export const getPostsReadyToPublish = async (): Promise<SocialMediaPost[]> => {
  try {
    const now = Timestamp.now();
    const postsRef = collection(getDb(), 'socialMediaPosts');
    const q = query(
      postsRef,
      where('status', '==', 'scheduled'),
      where('scheduledFor', '<=', now),
      orderBy('scheduledFor', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data() as Omit<SocialMediaPost, 'id'>
    }));
  } catch (error) {
    console.error('Error getting posts ready to publish:', error);
    return [];
  }
};

/**
 * Validate content against platform limits
 */
export const validateContent = (content: string, platforms: SocialPlatform[]): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const contentLength = content.length;

  for (const platform of platforms) {
    const limit = PLATFORM_LIMITS[platform].chars;
    if (contentLength > limit) {
      errors.push(`${platform}: Exceeds ${limit} character limit (currently ${contentLength})`);
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Extract hashtags from content
 */
export const extractHashtags = (content: string): string[] => {
  const hashtagRegex = /#(\w+)/g;
  const matches = content.match(hashtagRegex);
  if (!matches) return [];
  
  // Remove # and return unique hashtags
  return Array.from(new Set(matches.map(tag => tag.substring(1))));
};

/**
 * Get post statistics
 */
export const getPostStats = async () => {
  try {
    const postsRef = collection(getDb(), 'socialMediaPosts');
    const snapshot = await getDocs(postsRef);
    
    const posts = snapshot.docs.map(doc => doc.data() as SocialMediaPost);
    
    return {
      total: posts.length,
      drafts: posts.filter(p => p.status === 'draft').length,
      scheduled: posts.filter(p => p.status === 'scheduled').length,
      published: posts.filter(p => p.status === 'published').length,
      failed: posts.filter(p => p.status === 'failed').length,
      byPlatform: {
        facebook: posts.filter(p => p.platforms.includes('facebook')).length,
        instagram: posts.filter(p => p.platforms.includes('instagram')).length,
        linkedin: posts.filter(p => p.platforms.includes('linkedin')).length,
        tiktok: posts.filter(p => p.platforms.includes('tiktok')).length,
      }
    };
  } catch (error) {
    console.error('Error getting post stats:', error);
    return {
      total: 0,
      drafts: 0,
      scheduled: 0,
      published: 0,
      failed: 0,
      byPlatform: { facebook: 0, instagram: 0, linkedin: 0, tiktok: 0 }
    };
  }
};

/**
 * Duplicate a post (useful for creating similar posts)
 */
export const duplicatePost = async (id: string): Promise<string | null> => {
  try {
    const original = await getPostById(id);
    if (!original) {
      throw new Error('Original post not found');
    }

    const { id: _, createdAt, updatedAt, publishedAt, publishResults, ...postData } = original;
    
    return await createPost({
      ...postData,
      content: `${postData.content} (Copy)`,
      status: 'draft',
      scheduledFor: null,
    });
  } catch (error) {
    console.error(`Error duplicating post ${id}:`, error);
    return null;
  }
};
